import {Icon} from '../Icon';
import {cn} from '@/utils/common_functions';
import {Text} from '../Text';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  FocusEvent,
  useMemo,
} from 'react';
import {InputStateTypes} from '@/interface';
import {DateInput} from '../DateInput';
import {useClickOutside} from '@/hooks';

interface DateRangeInputInterface {
  label?: string;
  fromDate: Date | null;
  toDate: Date | null;
  name?: string;
  onFromDateChange: (value: Date | null) => void;
  onToDateChange: (value: Date | null) => void;
  disabled?: boolean;
  error?: string;
  touched?: boolean;
  placeholder?: string;
  required?: boolean;
  className?: string;
  iconClassName?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  textClassName?: string;
  onBlur?: (e: FocusEvent<HTMLButtonElement>) => void;
  onFocus?: (e: FocusEvent<HTMLButtonElement>) => void;
}

export const DateRangeInput: React.FC<DateRangeInputInterface> = props => {
  const {
    fromDate,
    toDate,
    className,
    disabled,
    error = '',
    iconClassName,
    wrapperClassName,
    inputClassName,
    textClassName,
    label,
    labelClassName,
    name,
    onFromDateChange,
    onToDateChange,
    placeholder = 'Date',
    required,
    touched,
    onBlur,
    onFocus,
  } = props;

  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [inputState, setInputState] = useState<InputStateTypes>('default');

  const ref = useClickOutside<HTMLDivElement>(() => {
    setDropdownOpen(false);
    updateInputState(error && touched ? 'error' : 'default');
  });

  const formatDateForPlaceholder = (date: Date | null): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}-${month}-${year}`;
  };

  const displayText = useMemo(() => {
    if (fromDate && toDate) {
      return `${formatDateForPlaceholder(fromDate)} to ${formatDateForPlaceholder(toDate)}`;
    } else if (fromDate) {
      return `From ${formatDateForPlaceholder(fromDate)}`;
    } else if (toDate) {
      return `To ${formatDateForPlaceholder(toDate)}`;
    }
    return '';
  }, [fromDate, toDate]);

  /**
   * a utility function to update input state with a check to disabled logic,
   */
  const updateInputState = useCallback(
    (state: InputStateTypes) => {
      if (disabled) {
        return;
      }
      setInputState(state);
    },
    [disabled],
  );

  /**
   * utility wrapper for onBlur handler, such that if also updates the input state locally
   */
  function onBlurHandler(e: FocusEvent<HTMLButtonElement>): void {
    updateInputState(error && touched ? 'error' : 'default');
    onBlur?.(e);
  }

  /**
   * utility wrapper for onFocus handler, such that if also updates the input state locally
   */
  function onFocusHandler(e: FocusEvent<HTMLButtonElement>): void {
    updateInputState('focus');
    onFocus?.(e);
  }

  const handleToggleDropdown = () => {
    if (!disabled) {
      setDropdownOpen(!dropdownOpen);
    }
  };

  useEffect(() => {
    if (touched && error) {
      updateInputState('error');
    } else {
      updateInputState('default');
    }
  }, [error, touched, updateInputState]);

  return (
    <div className={cn('flex flex-col gap-2 relative', className)} ref={ref}>
      {label && (
        <Text
          className={cn(
            'font-FigtreeSemiBold text-ui_blue text-base inline-flex gap-1',
            labelClassName,
          )}
          variant="body">
          {label}
          {required && <p className="text-rejection">*</p>}
        </Text>
      )}
      <div
        className={cn(
          'border-2 flex border-ui_input_border relative text-base rounded-lg items-center disabled:cursor-not-allowed w-full',
          inputState === 'error' && 'border-rejection',
          inputState === 'focus' && 'border-black',
          inputState === 'disabled' && 'border-x-ui_disabled',
          wrapperClassName,
        )}>
        <button
          type="button"
          onMouseDown={e => e.preventDefault()} // Prevents focus shift
          disabled={disabled}
          onClick={handleToggleDropdown}
          className="flex items-center">
          <Icon
            type="calender"
            className={cn('size-4 ml-4 cursor-pointer', iconClassName)}
          />
        </button>

        <button
          type="button"
          name={name}
          disabled={disabled}
          onClick={handleToggleDropdown}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          className={cn(
            'px-4 py-2 w-full rounded-lg self-stretch outline-none flex-1 min-w-0 bg-transparent text-left flex items-center',
            !displayText && 'text-ui_input_border_dark',
            textClassName,
          )}>
          <span className="whitespace-nowrap truncate">
            {displayText || placeholder}
          </span>
        </button>

        {/* Dropdown with two date inputs */}
        {dropdownOpen && (
          <div
            className={cn(
              'flex flex-col border max-h-[400px] w-full border-ui_input_border z-[999] rounded-lg bg-white absolute top-[104%] right-0 left-0 p-4 gap-4',
            )}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col gap-2">
              <Text className="font-FigtreeSemiBold text-sm text-ui_blue">
                From
              </Text>
              <DateInput
                value={fromDate}
                onChangeDate={onFromDateChange}
                placeholder="Select Date"
                wrapperClassName="border-[1px] w-full"
                className="w-full"
                name="fromDate"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Text className="font-FigtreeSemiBold text-sm text-ui_blue">
                To
              </Text>
              <DateInput
                value={toDate}
                onChangeDate={onToDateChange}
                placeholder="Select Date"
                wrapperClassName="border-[1px] w-full"
                className="w-full"
                name="toDate"
              />
            </div>
          </div>
        )}
      </div>

      {touched && error && (
        <Text className="text-rejection text-sm">{error}</Text>
      )}
    </div>
  );
};

